import {Injectable, OnDestroy} from '@angular/core';
import {environment} from '@env/environment';
import {FuseNavigationService, FuseVerticalNavigationComponent} from '@fuse/components/navigation';
import {AuthService} from '@shared/services/auth.service';
import {canAccessTeamChat} from 'app/navigation/nahaus.navigation';
import {combineLatest, of, Subscription, switchMap} from 'rxjs';
import {map} from 'rxjs/operators';

/**
 * Keeps the "Team Chat" entry in the main navigation up to date:
 * shows the number of unread messages (WhatsApp style) and falls back
 * to the "Beta" badge when everything has been read.
 *
 * Initialized once from the layout so it works app wide — not only
 * while the chat module is open.
 */
@Injectable({providedIn: 'root'})
export class TeamChatBadgeService implements OnDestroy {
    private static readonly NAV_ITEM_ID = 'apps.chat';
    private static readonly DEFAULT_BADGE = {title: 'Beta', classes: 'px-2 bg-primary text-on-primary rounded'};
    private static readonly UNREAD_BADGE_CLASSES = 'px-2 bg-warn text-on-warn rounded-full';

    private _subscription?: Subscription;

    constructor(
        private _authService: AuthService,
        private _fuseNavigationService: FuseNavigationService,
    ) {
    }

    ngOnDestroy(): void {
        this._subscription?.unsubscribe();
    }

    /**
     * Starts watching the user's team chats. Safe to call multiple times.
     */
    init(): void {
        if (this._subscription) {
            return;
        }

        // Follows the auth stream (no take(1)) so it survives logout/login
        this._subscription = combineLatest([this._authService.authUser$, this._authService.customerID$, this._authService.tier$]).pipe(
            switchMap(([user, customerID, tier]) => {
                if (!user?.uid || (environment.pool && !customerID) || !canAccessTeamChat(tier)) {
                    return of(0);
                }
                const path = customerID ? `customers/${customerID}/teamChats` : 'teamChats';
                return this._authService.afs
                    .collection<any>(path, ref => ref
                        .where('participantIds', 'array-contains', user.uid)
                        .orderBy('updatedAt', 'desc')
                        .limit(100))
                    .valueChanges()
                    .pipe(map((chats) => chats
                        // Ticket chats are isolated to the ticket — never count toward the team-chat badge
                        .filter((chat) => chat.type !== 'ticket')
                        .filter((chat) => !(chat.archivedBy || []).includes(user.uid))
                        .reduce((sum, chat) => sum + (chat.unreadCountBy?.[user.uid] || 0), 0)));
            }),
        ).subscribe({
            next: (unreadCount) => this._updateBadge(unreadCount),
            error: () => {
                this._subscription?.unsubscribe();
                this._subscription = undefined;
            },
        });
    }

    private _updateBadge(unreadCount: number): void {
        const navigationComponent = this._fuseNavigationService
            .getComponent<FuseVerticalNavigationComponent>('mainNavigation');
        if (!navigationComponent?.navigation) {
            return;
        }

        const menuItem = this._fuseNavigationService
            .getItem(TeamChatBadgeService.NAV_ITEM_ID, navigationComponent.navigation);
        if (!menuItem) {
            return;
        }

        menuItem.badge = unreadCount > 0
            ? {
                title: unreadCount > 99 ? '99+' : String(unreadCount),
                classes: TeamChatBadgeService.UNREAD_BADGE_CLASSES,
            }
            : {...TeamChatBadgeService.DEFAULT_BADGE};

        navigationComponent.refresh();
    }
}
